/**
 * ImpersonationBanner - the persistent act-as ("view as") banner and its exit
 * control (SPEC-FRONTEND-UI-049, SPEC-FRONTEND-RULE-095).
 *
 * IT IS A PURE FUNCTION OF ITS PROPS, AND THAT IS A SECURITY PROPERTY RATHER
 * THAN A STYLE. It fetches nothing, holds no TanStack query, reads no access
 * token and never touches `localStorage` or `sessionStorage`; it renders
 * identically for every viewer of the same props. Three surfaces read
 * impersonation state and SPEC-FRONTEND-RULE-077 / RULE-095(11) give the
 * descriptor ONE owning module with ONE key and ONE fetcher - the session
 * shell's (TASK-IMP-AUTHCONTEXT-SPA-WIRING). If this component fetched its own
 * descriptor it would be the SECOND fetcher on the `/me/impersonation` key,
 * TanStack keeps one cache entry per key against a five-minute global
 * staleness, and whichever surface mounted first would decide what the banner
 * showed. So the shell reads, the banner renders, and the exit is a callback
 * the shell implements. A future consumer that wants the banner to fetch
 * changes the shell's owning module, not this component (QUERY-KEY-GATE).
 *
 * THE COUNTDOWN COMES FROM THE BINDING ROW. {@link ImpersonationBinding.expires_at}
 * is the ONLY input to the remaining time. No duration, no platform lifetime
 * setting and no start time is a prop of this component, so there is no code
 * path in which a setting can reach the displayed number (grill ruling G4,
 * UI-049). The number is derived from the stored DEADLINE rather than from
 * accumulated ticks, so a backgrounded tab that misses ticks still shows the
 * correct remaining time when it returns.
 *
 * IT MOUNTS NOWHERE FROM HERE, AND IT RENDERS OUTSIDE EVERY FEATURE GATE. The
 * three SPA mounts (tenant / workspace / admin) belong to
 * TASK-IMP-AUTHCONTEXT-SPA-WIRING. This file imports nothing from
 * `web/src/feature-gate`, renders no `FeatureGate` and no `PermissionGate` and
 * reads no permission context, so flipping the kill switch can never strand a
 * live binding without its exit affordance (EPIC-IMPERSONATION-AC-004,
 * RULE-095(1)). That half is asserted by test here; the mount above the gates
 * is asserted in the wiring TASK.
 *
 * WHAT RENDERS, PER `state` (RULE-042's four data states plus the auth case):
 * - `loading`  - the strip with a skeleton row. Never an empty gap: an absent
 *   banner is indistinguishable from "no binding is live", which is exactly the
 *   confusion AC-004 forbids.
 * - `error`    - the strip with a LOUD visible `role="alert"` line. A failed
 *   descriptor read is never rendered as an absent banner.
 * - `terminal` - the terminal card: the typed end code's message and ONE button
 *   back to the return-to origin, with the page behind it still readable (no
 *   backdrop, no focus trap, nothing unmounted).
 * - `active` / `ending` with a `binding` - the live strip.
 * - `active` / `ending` with `binding` null - NOTHING AT ALL, not an empty
 *   element. There is no binding to announce.
 * A binding whose `expires_at` is already past renders the terminal card rather
 * than a negative countdown.
 *
 * "A REAL BUTTON" DOES NOT MEAN A RAW `<button>`. UI-049 asks for a real button
 * element as the banner's LAST focusable stop; MUI `Button` renders one. A
 * literal raw `<button>` in the JSX is flagged by `web/eslint.config.js`
 * `RAW_HTML_SELECTORS` and fails COMPONENT-GATE, which is how a correct reading
 * of the spec turns into a red gate if nobody says this out loud.
 *
 * REUSED, NOT RE-AUTHORED (RULE-033 / RULE-035): the one-minute warning IS the
 * canonical {@link ConfirmDialog} (two outcomes, destructive styling OFF, the
 * SAFE path - Stay - taking initial focus); the exit and the two overlay
 * buttons are MUI `Button`; every colour comes from `theme/colors` and every
 * radius from the PageShell token module.
 *
 * `LoadingState` IS DELIBERATELY NOT USED for the `loading` strip, and the
 * reason is recorded rather than omitted: its container is itself a
 * `role="status"` live region, and nesting one inside this strip's
 * `role="status"` would give the banner two live regions and make
 * `getByRole('status')` ambiguous for every consumer test. The skeleton variant
 * of `LoadingState` is a row of MUI `<Skeleton>` bars, which is exactly what
 * the strip renders directly. `<Skeleton>` is not a restricted primitive
 * (`RAW_PRIMITIVE_TO_WRAPPER` maps `CircularProgress`, which this file never
 * uses); the `ending` state uses the MUI Button's own `aria-busy`, per this
 * TASK's COMPONENT-GATE criterion.
 *
 * NO ESLINT REUSE-MAP ENTRY IS ADDED, AND THE REASON IS RECORDED. The banner
 * supersedes no MUI primitive and wraps no raw lowercase element - the strip is
 * a `Box` with `role="status"` - and `grep` finds zero raw `<header>`,
 * `<aside>` or `<nav>` elements in production source under `web/src`, so an
 * entry keyed on one of them would flag nothing while carrying a message that
 * is wrong for the element it named. Compare `AppFooter`, which DID add one
 * because it wraps a raw `<footer>`. The two inventory rows are the complete
 * RULE-035 clause (i) deliverable here.
 *
 * @see SPEC-FRONTEND-UI-049
 * @see SPEC-FRONTEND-RULE-095 clauses (1), (9), (11), (12)
 * @see SPEC-FRONTEND-RULE-035 (the new-shared-component Definition of Done)
 * @see TASK-IMP-BANNER-COMPONENT
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { colors } from '../../../theme/colors';
import { SURFACE_RADIUS } from '../PageShell/tokens';
import ConfirmDialog from '../ConfirmDialog';
import { LOCALE_TIME, formatLocaleDate } from '../../../utils/format';

/**
 * The typed end reason carried by every impersonation-end response
 * (SPEC-USER-AUTH-RULE-020: the binding's end reasons are PREFIXED so a literal
 * shared with the session table can never be read as the same event).
 */
export type ImpersonationEndCode =
  | 'imp_self_exit'
  | 'imp_admin_revoke'
  | 'imp_expired'
  | 'imp_idle'
  | 'imp_kill_switch'
  | 'imp_tenant_suspend'
  | 'imp_target_disabled'
  | 'imp_reauth';

/** The impersonated user, as the descriptor carries them. */
export interface ImpersonationTarget {
  /** The target's display name. Rendered in the identity line. */
  display_name: string;
  /** The target's email. Rendered beside the name so two same-named users differ. */
  email: string;
  /** The target's tenant name - NOT the impersonator's (RULE-095(3)). */
  tenant_name: string;
}

/** The intermediate identity of a two-deep chain (grill ruling G1). */
export interface ImpersonationVia {
  /** The intermediate identity's display name. */
  display_name: string;
}

/**
 * The act-as binding descriptor, exactly as `GET /api/v1/me/impersonation`
 * returns it and the session shell hands it down. Never fetched here.
 */
export interface ImpersonationBinding {
  /**
   * The NON-SECRET binding reference. Declared safe to show and log
   * (design section 2.1) - it is not a credential and gives nobody access -
   * but it is rendered only where UI-049 asks for it, which is nowhere in the
   * strip today. It is carried so the shell's exit callback and the tests can
   * identify the row.
   */
  session_ref: string;
  /** `view` is read-only; `act` may write. Rendered IN WORDS, never as a colour. */
  mode: 'view' | 'act';
  /** Chain depth. 1 is a direct binding; 2 names the intermediate identity. */
  depth: number;
  /** Who is being viewed. */
  target: ImpersonationTarget;
  /** The intermediate identity, present only on a depth-2 chain. */
  via?: ImpersonationVia;
  /**
   * THE BINDING ROW'S OWN DEADLINE, as an ISO-8601 instant. The ONLY source of
   * the countdown (grill ruling G4). A platform lifetime setting is never a
   * prop of this component and cannot reach the rendered number.
   */
  expires_at: string;
}

/**
 * The rendering state, decided by the session shell's read - not by this
 * component, which owns no query.
 */
export type ImpersonationBannerState =
  | 'loading'
  | 'active'
  | 'ending'
  | 'error'
  | 'terminal';

/** Props of {@link ImpersonationBanner}. */
export interface ImpersonationBannerProps {
  /** The descriptor, or `null` when no binding is live. */
  binding: ImpersonationBinding | null;
  /** Which rendering applies. See {@link ImpersonationBannerState}. */
  state: ImpersonationBannerState;
  /** The typed end code, rendered as the terminal card's reason. */
  terminalCode?: ImpersonationEndCode;
  /**
   * Called by the exit affordance. THE EXIT LIVES IN THE SHELL: this component
   * only calls the prop and never issues `DELETE /api/v1/impersonation/sessions/{ref}`
   * itself (SPEC-GO-API-CONTRACT-039, RULE-095(9)).
   */
  onExit: () => void;
  /** Called by the terminal card's ONE button, back to the return-to origin. */
  onReturn: () => void;
  /**
   * Label for the terminal card's button and, on a depth-2 chain, the exit
   * control - so the operator reads WHICH view they pop back to. Defaults to
   * "Back to administration".
   */
  returnLabel?: string;
}

/**
 * The strip's MINIMUM height in CSS px (UI-049's 48 px strip). It is a FLOOR,
 * never a fixed height: the Hungarian strings run roughly 60 percent longer
 * than the English ones, so the row wraps and the strip grows rather than
 * clipping (SPEC-FRONTEND-RULE-047). This is the ONE documented px literal this
 * component contributes (RULE-034).
 */
export const IMPERSONATION_BANNER_MIN_HEIGHT = 48;

/** The WCAG 2.1 AA pointer-target floor in CSS px (RULE-046 / RULE-007). */
const MIN_TARGET_SIZE = 24;

/**
 * Focus-ring width and offset in CSS px. The theme carries no focus-ring token
 * (`theme/themes.ts` defines none) and RULE-035 clause (c) requires a VISIBLE
 * focus indicator, so the value is named here rather than typed inline - the
 * same treatment `AppFooter` gives it.
 */
const FOCUS_RING_WIDTH = 2;

/** One tick per second. The DISPLAYED text only changes once a minute. */
const TICK_INTERVAL_MS = 1000;

/** Remaining minutes at or below which the warning chip appears (UI-049). */
const WARN_CHIP_MINUTES = 5;

/** Remaining minutes at or below which the confirm overlay offers exit-or-stay. */
const WARN_OVERLAY_MINUTES = 1;

const MS_PER_MINUTE = 60_000;

/**
 * Remaining WHOLE minutes until `deadlineMs`, rounded UP so a binding with
 * 13 minutes 40 seconds left reads 14 rather than 13. Returns 0 or less once
 * the deadline has passed, which is what switches the strip to the terminal
 * card. A deadline that does not parse yields `Number.NaN`, handled by the
 * caller as "no countdown" rather than as an expiry.
 */
const minutesUntil = (deadlineMs: number, nowMs: number): number =>
  Math.ceil((deadlineMs - nowMs) / MS_PER_MINUTE);

/** The fixed strip. A floor height, never a fixed one; it wraps and grows. */
const stripSx: SxProps<Theme> = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  minHeight: IMPERSONATION_BANNER_MIN_HEIGHT,
  // ONE LAYER ABOVE THE APP BAR (UI-049). The strip must sit over the shell it
  // annotates; it stays BELOW `drawer` (1200) and `modal` (1300), so the
  // one-minute ConfirmDialog - a portal at modal level - covers it, which is
  // the correct overlap for a dialog that asks about the strip itself.
  zIndex: (theme: Theme) => theme.zIndex.appBar + 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 0.5,
  px: 2,
  py: 1,
  borderBottom: '1px solid',
  borderColor: colors.border.default,
  // Opaque and load-bearing: the app shell scrolls UNDER a fixed strip, so a
  // transparent background would render the identity line over the content.
  backgroundColor: colors.status.warning,
  color: colors.text.primary,
};

/** The identity line. Wraps rather than clipping (RULE-047). */
const identitySx: SxProps<Theme> = {
  fontWeight: 600,
  color: colors.text.primary,
};

/** The secondary context line: tenant, logging notice. */
const contextSx: SxProps<Theme> = {
  color: colors.text.secondary,
};

/**
 * The five-minute chip. It carries TEXT, not only a colour - colour is never
 * the only carrier of the warning state (RULE-046 / WCAG 1.4.1).
 */
const chipSx: SxProps<Theme> = {
  px: 1,
  py: 0.25,
  borderRadius: SURFACE_RADIUS,
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: colors.status.error,
  color: colors.status.error,
  backgroundColor: colors.background.primary,
  fontWeight: 600,
};

/** The exit control: a real button, tokens only, at the target floor. */
const exitSx: SxProps<Theme> = {
  // Pushes the exit to the END of the row, which is also the END of the strip's
  // tab order - the LAST focusable element UI-049 requires.
  ml: 'auto',
  minHeight: MIN_TARGET_SIZE,
  minWidth: MIN_TARGET_SIZE,
  px: 1.5,
  textTransform: 'none',
  fontWeight: 600,
  borderColor: colors.text.primary,
  color: colors.text.primary,
  '&:hover': {
    borderColor: colors.text.primary,
    backgroundColor: colors.background.hover,
  },
  '&.Mui-disabled': {
    borderColor: colors.border.disabled,
    color: colors.text.disabled,
  },
  '&:focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: FOCUS_RING_WIDTH,
    outlineColor: colors.interactive.active,
    outlineOffset: FOCUS_RING_WIDTH,
  },
};

/** The terminal card. Same fixed placement; the page behind stays readable. */
const terminalSx: SxProps<Theme> = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  minHeight: IMPERSONATION_BANNER_MIN_HEIGHT,
  zIndex: (theme: Theme) => theme.zIndex.appBar + 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 1,
  px: 2,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: colors.status.error,
  backgroundColor: colors.background.secondary,
  color: colors.text.primary,
};

/**
 * The persistent act-as banner.
 *
 * @param props - See {@link ImpersonationBannerProps}.
 */
const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  binding,
  state,
  terminalCode,
  onExit,
  onReturn,
  returnLabel,
}) => {
  const { t } = useLingui();

  // The DEADLINE, parsed once per descriptor. Everything downstream derives
  // from it, so a missed tick can never accumulate into a wrong number.
  const deadlineMs = useMemo(
    () => (binding ? Date.parse(binding.expires_at) : Number.NaN),
    [binding],
  );

  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  // Stay DISMISSES and never extends: once the operator has chosen to stay, the
  // overlay does not come back and the countdown continues from where it was.
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  const minutesLeft = Number.isNaN(deadlineMs)
    ? Number.NaN
    : minutesUntil(deadlineMs, nowMs);

  const expired = !Number.isNaN(minutesLeft) && minutesLeft <= 0;
  const showTerminal = state === 'terminal' || (binding !== null && expired);

  // EXACTLY ONE TIMER, created on mount and cleared on unmount AND on the
  // transition to the terminal card. No other effect, subscription or listener
  // exists in this component.
  useEffect(() => {
    if (showTerminal || Number.isNaN(deadlineMs)) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [showTerminal, deadlineMs]);

  // A new descriptor is a new deadline, so the dismissal does not carry over.
  useEffect(() => {
    setOverlayDismissed(false);
  }, [deadlineMs]);

  const dismissOverlay = useCallback(() => setOverlayDismissed(true), []);

  const fallbackReturnLabel = t`Back to administration`;
  const resolvedReturnLabel = returnLabel ?? fallbackReturnLabel;

  // ── terminal card ────────────────────────────────────────────────────────
  if (showTerminal) {
    // A binding that ran out under us ended for exactly one reason, whatever
    // the shell has managed to learn from the server yet.
    const code: ImpersonationEndCode | undefined =
      state === 'terminal' ? terminalCode : 'imp_expired';

    let reason: string;
    switch (code) {
      case 'imp_expired':
        reason = t`The view has expired. Your work is not lost, but you must start again to continue.`;
        break;
      case 'imp_target_disabled':
        reason = t`This account was disabled, so the view has ended.`;
        break;
      case 'imp_kill_switch':
        reason = t`A platform administrator turned viewing off, so the view has ended.`;
        break;
      case 'imp_tenant_suspend':
        reason = t`The tenant is no longer active, so the view has ended.`;
        break;
      case 'imp_admin_revoke':
        reason = t`A platform administrator ended this view.`;
        break;
      case 'imp_idle':
        reason = t`The view ended because it was idle.`;
        break;
      case 'imp_reauth':
        reason = t`You signed in again, so the view has ended.`;
        break;
      case 'imp_self_exit':
        reason = t`You exited the view.`;
        break;
      default:
        reason = t`The view has ended.`;
        break;
    }

    return (
      <Box
        role="status"
        aria-live="polite"
        sx={terminalSx}
        data-testid="impersonation-banner-terminal"
      >
        <Typography variant="body2" sx={identitySx} data-testid="impersonation-banner-terminal-reason">
          {reason}
        </Typography>
        <Button
          type="button"
          variant="outlined"
          onClick={onReturn}
          sx={exitSx}
          data-testid="impersonation-banner-return"
        >
          {resolvedReturnLabel}
        </Button>
      </Box>
    );
  }

  // ── loading ──────────────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy
        aria-label={t`Loading the view details`}
        sx={stripSx}
        data-testid="impersonation-banner-loading"
      >
        <Skeleton
          variant="rounded"
          width="40%"
          height={MIN_TARGET_SIZE}
          sx={{ bgcolor: colors.background.tertiary }}
          data-testid="impersonation-banner-skeleton"
        />
      </Box>
    );
  }

  // ── error ────────────────────────────────────────────────────────────────
  // LOUD AND VISIBLE. A failed descriptor read is never rendered as an absent
  // banner: an absent banner is indistinguishable from "no binding is live"
  // (RULE-042, EPIC-IMPERSONATION-AC-004).
  if (state === 'error') {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={stripSx}
        data-testid="impersonation-banner-error"
      >
        <Typography role="alert" variant="body2" sx={identitySx}>
          {t`The view details could not be loaded.`}
        </Typography>
      </Box>
    );
  }

  // ── nothing to announce ──────────────────────────────────────────────────
  if (!binding) return null;

  const { mode, depth, target } = binding;
  const chained = depth >= 2 && Boolean(binding.via);

  // EVERY INTERPOLATED VALUE IS A PLAIN LOCAL IDENTIFIER, AND THAT IS NOT
  // COSMETIC. Lingui names an ICU placeholder after the expression only when
  // the expression is a bare identifier; a member expression such as
  // `target.display_name` extracts as the POSITIONAL `{0}`, and a positional
  // msgid is exactly how a Hungarian msgstr ends up binding a placeholder the
  // msgid does not carry - which renders the value EMPTY at runtime, with every
  // gate green (the 2026-09-18 fifty-string incident). Named here, both sides
  // of every catalog row carry the same `{name}`, `{email}`, `{tenant}`,
  // `{via}` and `{time}`.
  const name = target.display_name;
  const email = target.email;
  const tenant = target.tenant_name;
  const via = binding.via?.display_name ?? '';
  const time = formatLocaleDate(deadlineMs, LOCALE_TIME);

  let identity: string;
  if (chained) {
    identity = t`Acting as: ${name} - on behalf of ${via}`;
  } else if (mode === 'view') {
    identity = t`Viewing as: ${name} (${email})`;
  } else {
    identity = t`Acting as: ${name} (${email})`;
  }

  const ending = state === 'ending';
  // On a chain the exit pops ONE level, so the label says where the operator
  // lands rather than implying the whole chain ends (RULE-095(9)).
  const exitLabel = chained ? t`Back to ${via}'s view` : t`Exit view`;
  const showChip = !Number.isNaN(minutesLeft) && minutesLeft <= WARN_CHIP_MINUTES;
  const showOverlay =
    !Number.isNaN(minutesLeft) && minutesLeft <= WARN_OVERLAY_MINUTES && !overlayDismissed;

  return (
    <>
      <Box
        role="status"
        aria-live="polite"
        sx={stripSx}
        data-testid="impersonation-banner"
      >
        <Typography variant="body2" sx={identitySx} data-testid="impersonation-banner-identity">
          {identity}
        </Typography>

        {chained ? (
          // On a chain the identity line carries the intermediate identity
          // instead of the email, so the email is rendered here. An email
          // address is DATA, not UI text, so it carries no catalog entry.
          <Typography variant="caption" sx={contextSx} data-testid="impersonation-banner-email">
            {email}
          </Typography>
        ) : null}

        <Typography variant="caption" sx={contextSx} data-testid="impersonation-banner-tenant">
          {t`Tenant: ${tenant}`}
        </Typography>

        <Typography variant="caption" sx={contextSx} data-testid="impersonation-banner-notice">
          {t`Every action is logged under your name. This view expires at ${time}.`}
        </Typography>

        {!Number.isNaN(minutesLeft) ? (
          // `aria-live="off"` INSIDE the strip's polite region, deliberately:
          // the rendered text changes once a MINUTE, and without this the whole
          // strip would be re-announced on every rollover. The manual
          // screen-reader pass of RULE-035 clause (d) checks exactly this.
          <Typography
            variant="caption"
            aria-live="off"
            sx={contextSx}
            data-testid="impersonation-banner-countdown"
          >
            {plural(minutesLeft, {
              one: 'Expires in # minute',
              other: 'Expires in # minutes',
            })}
          </Typography>
        ) : null}

        {showChip ? (
          <Typography
            variant="caption"
            component="span"
            sx={chipSx}
            data-testid="impersonation-banner-chip"
          >
            {t`Expiring soon`}
          </Typography>
        ) : null}

        {/*
          THE LAST FOCUSABLE ELEMENT OF THE STRIP (UI-049). Nothing focusable
          may be rendered after it. MUI Button renders a real <button>, which is
          what "a real button" means here - a literal raw <button> fails
          COMPONENT-GATE.
        */}
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={onExit}
          disabled={ending}
          aria-busy={ending}
          sx={exitSx}
          data-testid="impersonation-banner-exit"
        >
          {ending ? t`Exiting...` : exitLabel}
        </Button>
      </Box>

      {/*
        THE ONE-MINUTE WARNING IS THE CANONICAL ConfirmDialog, never a raw
        Dialog (COMPONENT-GATE). Two outcomes, destructive styling OFF, and the
        SAFE path - Stay - takes initial focus (RULE-044). STAY DISMISSES AND
        NEVER EXTENDS: it sets no deadline, touches no binding and the countdown
        continues from where it was.
      */}
      <ConfirmDialog
        open={showOverlay}
        title={t`Your view is about to expire`}
        message={t`Your view expires in less than a minute. Exit now, or stay and let it end.`}
        confirmText={t`Exit now`}
        cancelText={t`Stay`}
        onConfirm={onExit}
        onCancel={dismissOverlay}
        destructive={false}
        initialFocus="cancel"
      />
    </>
  );
};

export default ImpersonationBanner;
